import React, { Fragment, useEffect } from 'react';
import type { FC } from 'react';
import { useFormState } from 'react-final-form';
import { Field } from '@alfa-bank/site-form-utils/components/form-field';
import { BlockHolder } from '@alfa-bank/site-block-holder';
import { BackgroundV2 as Background } from '@alfa-bank/site-background-v2';
// TODO: https://jira.moscow.alfaintra.net/browse/ALFABANKRU-83779
// eslint-disable-next-line package-imports/no-core-components-imports
import { Switch } from '@alfalab/core-components-switch';
import { Link } from '@alfa-bank/site-link';
import { StaticActions as actions } from '@alfa-bank/site-form-utils/actions';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import { useDispatch } from 'react-redux';
import { setBrokerageCheckbox } from '@alfa-bank/site-dc-form-api/store';

import { deliveryStepAnalytics as analytics } from '../../../../analytics';

import type { TBrokeragePlate } from './model';

export const BrokeragePlate: FC<TBrokeragePlate> = ({
    Text,
    actionText,
    backgroundColor = '#EEEDFF',
    backgroundImg,
    checkedByDefault = true,
    description = '',
    headerText,
    isMobile,
    links,
    subheaderText,
}) => {
    const { values } = useFormState({ subscription: { values: true } });
    const dispatch = useDispatch();

    useEffect(() => {
        if (values.deliveryType === 'courier') {
            sendEvent(analytics.brokerage.view(checkedByDefault));
            dispatch(setBrokerageCheckbox(Boolean(checkedByDefault)));
        }
    }, [checkedByDefault, values.deliveryType, dispatch]);

    return (
        <Field
            initialValue={checkedByDefault}
            name="brokerageAccountRequired"
            render={({ input, meta }) => {
                return (
                    <BlockHolder styler={{ marginBottom: 0 }}>
                        <Background
                            color={backgroundColor}
                            dataTestId="dc-form-brokerage-plate"
                            image={backgroundImg}
                            styler={
                                isMobile
                                    ? { borderRadius: 16, padding: 16, marginTop: 24 }
                                    : { borderRadius: 24, padding: 24, marginTop: 40 }
                            }
                        >
                            <Text styler={{ margin: 0, marginBottom: 4 }} view="xsmall">
                                {headerText}
                            </Text>

                            {subheaderText && (
                                <Text styler={{ margin: 0, marginBottom: 8 }} view="primary-medium">
                                    {subheaderText}
                                </Text>
                            )}

                            {description && (
                                <Text styler={{ margin: 0, marginBottom: 8 }} view="xsmall">
                                    {description}
                                </Text>
                            )}

                            <BlockHolder alignX="left">
                                <div>
                                    {links.map(({ href, text }, index) => (
                                        <Fragment key={index}>
                                            <Link href={href} target="_blank">
                                                <Text
                                                    kind="caption"
                                                    tag="span"
                                                    view="component-secondary"
                                                >
                                                    {text}
                                                </Text>
                                            </Link>

                                            {index !== links.length - 1 && (
                                                <Text
                                                    kind="caption"
                                                    tag="span"
                                                    view="component-secondary"
                                                >
                                                    ,{' '}
                                                </Text>
                                            )}
                                        </Fragment>
                                    ))}
                                </div>
                            </BlockHolder>

                            <BlockHolder styler={{ marginTop: 16 }}>
                                <Switch
                                    block
                                    checked={input?.value}
                                    dataTestId="dc-form-brokerage-plate-switch"
                                    label={actionText}
                                    onChange={(_, payload) => {
                                        if (payload) {
                                            actions.onChange(input, meta)(payload.checked);

                                            dispatch(
                                                setBrokerageCheckbox(Boolean(payload.checked))
                                            );
                                            sendEvent(analytics.brokerage.click(payload.checked));
                                        }
                                    }}
                                />
                            </BlockHolder>
                        </Background>
                    </BlockHolder>
                );
            }}
        ></Field>
    );
};
